import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck, AlertTriangle, FileText, Send, Download, UserPlus, X, Lock, Check,
} from 'lucide-react';
import { needsTerminalId, activeSheetFor, type MerchantMessageRole } from '@platform/shared-types';
import {
  merchantApi, MERCHANT_STATUS_CHIP, MERCHANT_STATUS_LABEL,
  type MerchantApplicationView, type IsoPartnerRow,
} from '../../services/merchantApi';
import { toastService } from '../../services/toastService';
import { useDataVersion } from '../../services/dataChangeBus';
import type { DeviceOrderRow, DeviceUnitRow, DeviceModelRow } from '../../services/hardwareApi';
/* The same approval section the card-processing lane shows — one component,
   so "approved" cannot mean two different things on two screens. */
import SurchargeApprovalSection from '../../components/pos/SurchargeApprovalSection';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * MERCHANT PROCESSING, on the order that is waiting for it.
 *
 * The third surface. The tenant submits documents in the setup wizard; a super
 * admin verifies them on the location's Registrations tab; NEITHER of those
 * can move an order. This one can — it is where the application is handed to
 * an ISO partner, where the rate quote and VAR sheets land, and where the
 * conversation between the three parties lives.
 *
 * TWO TRACKS, ONE GATE. RubyTill's own compliance pack never blocks anything.
 * What this panel shows, and the only thing that holds a device back, is
 * whether every payment unit on the order has been keyed to a terminal id.
 */

const chip = (s: string) => MERCHANT_STATUS_CHIP[s] || 'bg-gray-200 text-gray-700';

const when = (ts?: number) =>
  ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const whenTime = (ts?: number) =>
  ts ? new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

interface Props {
  order: DeviceOrderRow;
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  locationName?: string;
  onChanged?: () => void;
  /** Hands the loaded application up, so the stage timeline can resolve from it. */
  onApplication?: (app: MerchantApplicationView | null) => void;
  /**
   * Which stage is rendering this. 'selection' is the partner choice; 'iso' is
   * everything that happens while it is on their side — the sheet, the
   * documents they ask for, and the thread. Omitted renders the lot.
   */
  only?: 'selection' | 'iso';
}

const MerchantPanel: React.FC<Props> = ({ order, units, models, locationName, onChanged, onApplication, only }) => {
  /*
    ONE PANEL, TWO STAGES.

    Choosing a partner and waiting on that partner are different pieces of
    work owned by different people, and stacking them in one block meant the
    conversation — the thing that actually moves an application along — was
    buried under a partner choice that was settled days ago. `only` splits the
    same component across the two stages rather than forking it, so the two
    can never drift apart.
  */
  const show = (k: 'selection' | 'iso') => !only || only === k;
  const [app, setApp] = useState<MerchantApplicationView | null>(null);
  const [partners, setPartners] = useState<IsoPartnerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const dataVersion = useDataVersion();

  const [picking, setPicking] = useState(false);

  /*
    THE ESCAPE HATCH for a BYO application that has gone quiet.

    `sourceOverride` is local-only — it never writes `merchantSource` back to
    the record. Confirming the handover just unlocks the same partner-picking
    UI a `new` application already gets; the real state change is the
    `assign` call that flow already makes, same as it always has.
  */
  const [sourceOverride, setSourceOverride] = useState<'new' | null>(null);
  const [handoverOpen, setHandoverOpen] = useState(false);

  /*
    THE HANDOVER IS ABOUT ONE APPLICATION, so none of its state may outlive it.

    This panel is rendered from OrderDetail, which OrdersPage renders without a
    `key` — switching orders through the `open` search param reuses this very
    instance and refreshes `app` underneath it. Without this, a handover begun
    on one order shows its unlocked picker on the next.
  */
  useEffect(() => {
    setSourceOverride(null);
    setHandoverOpen(false);
    setPicking(false);
  }, [app?.applicationId]);

  /**
   * THE PAYMENT UNITS ON THIS ORDER — the only ones the gate holds back.
   *
   * `needsTerminalId` is the shared predicate, not a category test written
   * again here: a device treated as a payment device on this screen and a
   * peripheral in fulfilment produces an order that is at once blocked and
   * shippable.
   */
  const paymentUnits = useMemo(() => {
    const byId = new Map(models.map((m) => [m.modelId, m]));
    return units.filter((u) => needsTerminalId(byId.get(u.modelId) as any));
  }, [units, models]);

  /* `nonPaymentUnits` and `readiness` lived here for the dispatch-readiness
     tile. The tile is gone — see the note where it stood — and nothing else
     read them, so they went with it rather than lingering as a second,
     unrendered definition of "ready". */

  /**
   * At least one card reader on this order has no live, bound VAR sheet.
   *
   * `activeSheetFor` (shared-types) is the one rule for "bound and not
   * superseded" — repeating it here as `varSheets.length > 0` is exactly the
   * bug this exists to avoid: a merchant who sent one sheet of three reading
   * as fully covered.
   */
  const hasUnboundPaymentUnit = useMemo(
    () => paymentUnits.some((u) => !activeSheetFor(app?.varSheets, u.unitId)),
    [paymentUnits, app?.varSheets],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      // Idempotent by construction — the id derives from the location. Opening
      // this panel is what backfills a store created before the track existed.
      const ensured = await merchantApi.ensureApplication(order.tenantId, order.locationId, locationName);
      setApp(ensured.application);
      onApplication?.(ensured.application);
      if (!ensured.application.assignedIsoId) {
        const p = await merchantApi.listPartners().catch(() => ({ partners: [] as IsoPartnerRow[] }));
        setPartners(p.partners);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not read the merchant application for this store.');
    } finally {
      setLoading(false);
    }
  // dataVersion: re-read in place after a save anywhere on the order screen.
  }, [order.tenantId, order.locationId, locationName, onApplication, dataVersion]);

  useEffect(() => { load(); }, [load]);

  const run = async (label: string, fn: () => Promise<{ application: MerchantApplicationView }>) => {
    setBusy(label);
    try {
      const res = await fn();
      setApp(res.application);
      onApplication?.(res.application);
      onChanged?.();
    } catch (e: any) {
      toastService.error(e?.message || 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  const openFile = async (kind: 'document' | 'quote' | 'var', id: string) => {
    if (!app) return;
    try {
      // Minted per click and never cached — see merchantApi.fileUrl.
      const { url } = await merchantApi.fileUrl(app.tenantId, app.applicationId, kind, id);
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not open that file.');
    }
  };

  const send = async () => {
    if (!app || !draft.trim()) return;
    /*
      ONE AUDIENCE: everyone on the application.

      The partner, the merchant and RubyTill all read the same thread. A
      private lane here produced a conversation the customer could not see
      about the rates they were being offered — and the moment one exists,
      every party starts wondering what is in it.
    */
    const to: MerchantMessageRole[] = ['iso', 'tenant'];
    await run('message', () => merchantApi.postMessage(app.tenantId, app.applicationId, draft.trim(), to));
    setDraft('');
  };

  if (loading) {
    return (
      <RubyLoader variant="section" className="bg-white rounded-2xl shadow-sm border border-stone-200/60" />
    );
  }

  if (error || !app) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-stone-800">Merchant processing is unavailable</p>
            <p className="text-xs text-stone-500 mt-1">{error}</p>
            <button onClick={load} className="mt-3 px-3 py-2 rounded-lg border border-stone-200 text-xs font-semibold text-stone-700 hover:bg-stone-50">
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className={only
      ? 'space-y-4'
      : 'grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start'}>
      <div className="space-y-4">

        {show('selection') && (<>
        {/* ── the application ─────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500">Merchant processing</h3>
            <span className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold ${chip(app.status)}`}>
              {MERCHANT_STATUS_LABEL[app.status] || app.status}
            </span>
          </div>

          <p className="text-[13px] text-stone-600 mt-3 leading-relaxed">{app.summary.headline}</p>

          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-stone-500">
            {app.profile?.mccCode && <span>MCC <b className="text-rose-700">{app.profile.mccCode}</b></span>}
            {app.merchantSource && (
              <span>{app.merchantSource === 'existing' ? 'Brought their own processor' : 'Needs setting up'}</span>
            )}
            {app.assignedIsoName && <span>Partner: <b className="text-stone-700">{app.assignedIsoName}</b> · {when(app.assignedAt)}</span>}
          </div>

          {/* Assignment */}
          {!app.assignedIsoId && (app.merchantSource !== 'existing' || sourceOverride === 'new') && (
            <div className="mt-4">
              {!picking ? (
                <button
                  onClick={() => setPicking(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold min-h-[38px]"
                >
                  <UserPlus size={14} /> Assign to a partner
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500">Assign to a partner</p>
                    <button
                      onClick={() => {
                        setPicking(false);
                        // Closed without assigning — bring the amber trigger
                        // back. A successful assign has already set
                        // app.assignedIsoId, which gates the trigger off
                        // regardless, so this is a no-op in that case.
                        if (!app.assignedIsoId) setSourceOverride(null);
                      }}
                      aria-label="Close"
                      className="text-stone-400 hover:text-stone-600"
                    >
                      <X size={15} />
                    </button>
                  </div>
                  {partners.length === 0 && (
                    <p className="text-[12px] text-stone-500">No partner has merchant processing switched on yet.</p>
                  )}
                  {partners.map((p) => {
                    const mcc = app.profile?.mccCode;
                    const writes = !mcc || !p.capabilities.mccCodes?.length || p.capabilities.mccCodes.includes(mcc);
                    const usable = p.acceptingOrders && writes;
                    return (
                      <div
                        key={p.id}
                        className={`rounded-xl border p-3 ${usable ? 'border-stone-200 bg-white' : 'border-stone-200 bg-stone-50 opacity-70'}`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <p className="text-[13px] font-semibold text-stone-900">{p.name || p.id}</p>
                            <p className="text-[11.5px] text-stone-500 mt-1">
                              {p.openCount} open
                              {p.medianDaysToApproval !== null && ` · median ${p.medianDaysToApproval} days to approval`}
                              {p.medianDaysToApproval === null && ' · no approvals yet'}
                            </p>
                            <div className="flex gap-1.5 mt-2 flex-wrap">
                              {mcc && (
                                <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${writes ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {writes ? `Writes ${mcc}` : `Does not write ${mcc}`}
                                </span>
                              )}
                              {!p.acceptingOrders && (
                                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-200 text-gray-700">Paused</span>
                              )}
                            </div>
                          </div>
                          <button
                            disabled={busy === 'assign'}
                            onClick={() => run('assign', () => merchantApi.assign(app.tenantId, app.applicationId, p.id))
                              .then(() => setPicking(false))}
                            className="px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold disabled:opacity-50"
                          >
                            Assign
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-[11.5px] text-stone-400 leading-relaxed">
                    A partner who does not write this MCC can still be assigned, but the service will ask you to
                    say why — an application that bounces a week later costs the customer the week.
                  </p>
                </div>
              )}
            </div>
          )}

          {/*
            THE ESCAPE HATCH — a BYO application that has gone quiet.

            Assignment stays hidden on the `existing` path once every reader
            already has a live sheet: there is nothing a partner needs to
            issue. It reappears the moment even one reader is unbound, because
            that is exactly the merchant who said yes and then stopped
            answering — the order cannot ship on a promise.
          */}
          {!app.assignedIsoId && app.merchantSource === 'existing' && sourceOverride !== 'new' && hasUnboundPaymentUnit && (
            <div className="mt-4 border-l-[3px] border-amber-400 bg-amber-50/70 rounded-r-xl px-4 py-3">
              <p className="text-[12.5px] text-stone-700 leading-relaxed">
                <b className="text-stone-900">Not sending the rest?</b> Hand this to a payments partner.
                They will issue sheets for whatever is still unbound — anything the merchant already sent
                stays exactly as it is.
              </p>
              <button
                onClick={() => setHandoverOpen(true)}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold min-h-[44px]"
              >
                Hand to a payments partner
              </button>
            </div>
          )}

          {app.assignedIsoId && (
            <button
              disabled={busy === 'unassign'}
              onClick={() => run('unassign', () => merchantApi.unassign(app.tenantId, app.applicationId))}
              className="mt-4 px-3 py-2 rounded-lg border border-stone-200 text-[11px] font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50"
            >
              Take it back off {app.assignedIsoName || 'them'}
            </button>
          )}
        </section>

        </>)}

        {show('iso') && (<>
        {/* ── documents the processor asked for ───────────────────────── */}
        {app.documents.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
            <h3 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 mb-3">
              Documents · {app.documents.filter((d) => d.status === 'accepted' || d.status === 'uploaded').length} of {app.documents.length} in
            </h3>
            <div className="space-y-2">
              {app.documents.map((d) => (
                <div key={d.documentId} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-stone-50 border border-stone-200">
                  <FileText size={15} className="text-stone-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-stone-900 truncate">{d.name}</p>
                    <p className="text-[11px] text-stone-500 mt-0.5">
                      {d.status === 'requested' ? 'Not uploaded' : d.status}
                      {d.uploadedAt ? ` · ${when(d.uploadedAt)}` : ''}
                      {d.comment ? ` · ${d.comment}` : ''}
                    </p>
                  </div>
                  {d.sensitive && (
                    <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                      <Lock size={10} /> Sensitive
                    </span>
                  )}
                  {d.hasFile && (
                    <button onClick={() => openFile('document', d.documentId)} className="px-2.5 py-1.5 rounded-lg border border-stone-200 text-[11px] font-semibold text-stone-700 hover:bg-white">
                      <Download size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11.5px] text-stone-400 mt-3 leading-relaxed">
              Opening a sensitive document is written to this record's access log, which the merchant can see.
            </p>
          </section>
        )}

        {/* ── the rate quote ──────────────────────────────────────────── */}
        {app.activeQuote && (
          <section className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
            <h3 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 mb-3">Rate schedule</h3>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-stone-50 border border-stone-200">
              <FileText size={15} className="text-rose-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-stone-900 truncate">
                  {app.activeQuote.fileName || 'Rate schedule'}
                </p>
                <p className="text-[11px] text-stone-500 mt-0.5">
                  {app.activeQuote.summary || 'Sent by the partner'} · {when(app.activeQuote.uploadedAt)}
                </p>
              </div>
              <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                app.activeQuote.status === 'accepted' ? 'bg-emerald-100 text-emerald-700'
                : app.activeQuote.status === 'declined' ? 'bg-red-100 text-red-700'
                : 'bg-amber-100 text-amber-700'}`}
              >
                {app.activeQuote.status === 'proposed' ? 'With the merchant' : app.activeQuote.status}
              </span>
              {app.activeQuote.hasFile && (
                <button onClick={() => openFile('quote', app.activeQuote!.quoteId)} className="px-2.5 py-1.5 rounded-lg border border-stone-200 text-[11px] font-semibold text-stone-700 hover:bg-white">
                  <Download size={12} />
                </button>
              )}
            </div>
            {app.activeQuote.declineReason && (
              <p className="text-[12px] text-stone-600 mt-2">Declined: {app.activeQuote.declineReason}</p>
            )}
            <p className="text-[11.5px] text-stone-400 mt-3 leading-relaxed">
              Only the merchant can accept or decline this. You can see which — never decide for them.
            </p>
          </section>
        )}

        {/* ── surcharge agreement ─────────────────────────────────────
            Beside the rate quote, because it is the same KIND of thing: a
            signed document that sets what this merchant may charge. The
            quote is what the processor charges them; this is what they may
            charge their customer. Both are per location, both arrive from
            the ISO, and keeping them apart is what made the surcharge step
            easy to miss on an order.

            It is the gate on the per-register switch further down the order,
            so it has to be reachable from the lane where the paperwork is
            being done — not only from the location's own settings page. */}
        <section className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
          <h3 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 mb-3">
            Surcharge agreement
          </h3>
          <SurchargeApprovalSection
            tenantId={order.tenantId}
            locationId={order.locationId}
            locationName={locationName}
            onChanged={() => onChanged?.()}
          />
        </section>

        {/* ── the conversation ────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
          <h3 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 mb-3">Conversation</h3>
          {/*
            A CHAT WINDOW, not a stack of grey boxes.

            Three parties talk here and it took reading each header to tell
            who said what. RubyTill's own messages sit on the RIGHT as "You" —
            this is our screen — with the partner and the merchant on the left
            in their own colours, the same arrangement the partner and the
            merchant each see from their side.

            THE AUDIENCE STAYS ON EVERY MESSAGE. It is the one thing a chat
            bubble would otherwise lose, and on this thread it is the thing
            that matters most: "Partner only" and "Everyone" are not the same
            sentence, and an operator quoting the wrong one to a merchant is
            the mistake this label exists to prevent.
          */}
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {app.thread.length === 0 && (
              <p className="text-[12px] text-stone-400">Nothing said yet.</p>
            )}
            {app.thread.map((m) => {
              // Older messages may carry a narrower audience from before this
              // thread became open to all three; they are labelled honestly.
              const to = m.audience || [];
              const label = to.includes('tenant') && to.includes('iso') ? 'Everyone'
                : to.length === 0 ? 'Internal (historic)'
                : to.includes('tenant') ? 'Merchant' : 'Partner only';
              const mine = m.authorRole === 'super_admin';
              const skin = mine
                ? 'bg-rose-50 border-rose-200'
                : m.authorRole === 'iso' ? 'bg-slate-50 border-slate-200' : 'bg-amber-50 border-amber-200';
              const avatar = mine ? 'bg-rose-600' : m.authorRole === 'iso' ? 'bg-slate-600' : 'bg-amber-600';
              const nameColour = mine ? 'text-rose-700' : m.authorRole === 'iso' ? 'text-slate-700' : 'text-amber-700';
              const role = m.authorRole === 'iso' ? 'payments partner' : m.authorRole === 'tenant' ? 'merchant' : null;

              return (
                <div key={m.messageId} className={`flex gap-2.5 items-start ${mine ? 'flex-row-reverse' : ''}`}>
                  <span className={`w-7 h-7 rounded-lg grid place-items-center text-[10px] font-extrabold text-white shrink-0 ${avatar}`}>
                    {(m.authorName || '?').trim().slice(0, 2).toUpperCase()}
                  </span>
                  <div className={`min-w-0 flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`text-[11px] font-bold ${nameColour}`}>
                        {mine ? 'You' : m.authorName}
                      </span>
                      {role && (
                        <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">{role}</span>
                      )}
                      <span className="text-[10.5px] text-stone-400">{whenTime(m.at)}</span>
                      <span className={`text-[9.5px] px-1.5 py-0.5 rounded font-bold ${
                        label === 'Everyone' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}
                      >
                        {label}
                      </span>
                    </div>
                    <p className={`text-[13px] leading-relaxed whitespace-pre-wrap px-3.5 py-2.5 rounded-2xl border mt-1.5 max-w-[520px] text-stone-800 ${skin}`}>
                      {m.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Write a message…"
              className="flex-1 min-w-[200px] px-3.5 py-2.5 rounded-lg border border-stone-200 text-[12.5px] text-stone-800 focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/10"
            />
            <button
              disabled={!draft.trim() || busy === 'message'}
              onClick={send}
              className="px-3.5 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busy === 'message' ? <RubyGemMark size={12} drawing color="currentColor" /> : <Send size={12} />} Send
            </button>
          </div>
          <p className="text-[11.5px] text-stone-400 mt-2.5 leading-relaxed">
            <b className="text-stone-500">Everyone sees everything here</b> — the merchant, the partner and us.
            There is no private lane, on purpose: a hidden conversation about somebody's rates is one they will
            eventually find out about.
          </p>
        </section>
        </>)}
      </div>

      {/* ── right rail ────────────────────────────────────────────────── */}
      {show('iso') && (
      <div className="space-y-4">
        {/*
          DISPATCH READINESS USED TO BE HERE — a tile counting keyed TIDs.

          It was removed, not moved. It restated, in a second vocabulary, what
          "VAR sheets → readers" says directly above it: whether every reader
          has its paperwork. Now that a bound sheet is what releases a reader
          (`dispatchReadiness`), the tile's "0 of 1 payment units keyed" was
          counting a step that is no longer the one being waited on — and two
          boxes disagreeing about whether an order can ship is worse than one
          box saying it plainly.
        */}
        {/*
          THE FLAT "VAR SHEETS" LIST WAS REMOVED.

          It dumped every sheet on the application by MID — and for a merchant
          who hands over a statement rather than a sheet that reads "processor
          not named · 0 TIDs", which tells an operator nothing and looks like a
          fault. Everything it carried is in "VAR sheets → readers" above,
          against the reader each sheet actually programs, with the superseded
          ones kept and struck through. A sheet with no reader is not a fact
          worth its own box.
        */}

        <div className="border-l-[3px] border-rose-500 bg-rose-50/70 rounded-r-xl px-3.5 py-3 text-[12px] leading-relaxed text-stone-600">
          <b className="text-stone-800">The sheet is the evidence, not the status.</b> A store that brought its own
          processor never reaches "approved" and still ships — what counts is whether this reader has a VAR sheet
          bound to it, or a terminal id keyed off one.
        </div>

        {app.accessLog && app.accessLog.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
            <h3 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 mb-2 inline-flex items-center gap-1.5">
              <ShieldCheck size={12} /> Who looked
            </h3>
            {app.accessLog.slice(-6).reverse().map((a, i) => (
              <p key={i} className="text-[11.5px] text-stone-500 py-1 border-b border-stone-200 last:border-0">
                <b className="text-stone-700">{a.by}</b> {a.action} · {whenTime(a.at)}
              </p>
            ))}
          </section>
        )}
      </div>
      )}
    </div>

    {/*
      THE CONFIRMATION — a custom modal, never `window.confirm()`. Lists every
      reader and what actually happens to it: a sheet already bound stays
      bound and in force until a partner's sheet supersedes it (nothing here
      clears or deletes it); a reader with nothing bound is the one the
      partner will issue for. When at least one reader keeps its current
      sheet, the store ends up split across two merchant accounts, and that
      is said plainly rather than discovered later.
    */}
    {handoverOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        onClick={() => setHandoverOpen(false)}
      >
        <div
          className="w-full max-w-[560px] rounded-2xl bg-white shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-4 border-b border-stone-200">
            <h3 className="text-[15px] font-bold text-stone-900">Hand to a payments partner?</h3>
            <p className="text-[12.5px] text-stone-500 mt-1">
              {app.locationName || locationName || 'This location'} · {paymentUnits.length} card reader{paymentUnits.length === 1 ? '' : 's'}
            </p>
          </div>

          <div className="px-5 py-4">
            <p className="text-[12.5px] text-stone-600 mb-3">Nothing is deleted. Here is where each reader stands:</p>

            {paymentUnits.map((u) => {
              const sheet = activeSheetFor(app.varSheets, u.unitId);
              return (
                <div
                  key={u.unitId}
                  className="rounded-xl border border-stone-200 px-3.5 py-2.5 mb-2 last:mb-0 flex items-center gap-3"
                >
                  <span className={`w-6 h-6 rounded-lg grid place-items-center shrink-0 text-white ${
                    sheet ? 'bg-emerald-500' : 'bg-amber-500'}`}
                  >
                    {sheet ? <Check size={13} /> : <span className="w-2 h-2 rounded-full bg-white" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-semibold text-stone-800 font-mono truncate">{u.unitId}</p>
                    <p className="text-[11.5px] text-stone-500">
                      {sheet
                        ? <>Keeps the merchant's sheet{sheet.mid ? <> · MID {sheet.mid}</> : ''}</>
                        : 'Nothing bound — the partner will issue one'}
                    </p>
                  </div>
                </div>
              );
            })}

            {paymentUnits.some((u) => activeSheetFor(app.varSheets, u.unitId)) && (
              <div className="mt-3.5 rounded-xl bg-stone-50 border border-stone-200 px-3.5 py-3">
                <p className="text-[12px] text-stone-600 leading-relaxed">
                  A reader with a sheet bound today stays on the merchant's own account unless you
                  later bind a partner sheet to it. <b className="text-stone-800">This store will be
                  split across two merchant accounts</b> — allowed, and worth knowing before you do it.
                </p>
              </div>
            )}
          </div>

          <div className="px-5 py-3.5 border-t border-stone-200 flex items-center justify-end gap-2">
            <button
              onClick={() => setHandoverOpen(false)}
              className="px-4 py-2.5 rounded-xl border border-stone-200 text-stone-700 hover:bg-stone-50 text-sm font-semibold min-h-[44px]"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // See the note on `sourceOverride` above — this never writes
                // `merchantSource`. It unlocks the same partner-picking UI a
                // `new` application gets, and opens it straight away.
                setSourceOverride('new');
                setHandoverOpen(false);
                setPicking(true);
              }}
              className="px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold min-h-[44px]"
            >
              Hand it over
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default MerchantPanel;
